import { featureFlags } from '@seen/core/featureFlags'
import { Collapsible, cn } from '@seen/ui'
import {
  type ComponentPropsWithoutRef,
  type JSX,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react'
import { Card } from '#src/components/core/Card.tsx'
import { FacesheetCardContext } from '#src/components/patient/detail-content/facesheet/FacesheetCardContext.ts'
import {
  type FacesheetCardName,
  reportFacesheetCardToggled,
} from '#src/components/patient/detail-content/facesheet/facesheetObservability.ts'
import { useFacesheetTab } from '#src/components/patient/detail-content/facesheet/FacesheetTabContext.ts'
import { useFacesheetCardCollapseStore } from '#src/hooks/useFacesheetCardCollapseStore.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

export interface FacesheetCardProps extends ComponentPropsWithoutRef<'div'> {
  // Sections that are reference material rather than the reason the facesheet
  // is open can start folded away.
  defaultOpen?: boolean
  // Cards with nothing under the header — an Allergies card for a participant
  // with no allergies — pass false so the header keeps no triangle and no
  // toggle.
  collapsible?: boolean
  // Names the card in its RUM collapse and expand actions.
  rumName?: FacesheetCardName
}

// Card wrapper for facesheet cards. Owns the whole facesheet card spec — a
// 12px-padded column with an 8px gap between the header row and the content —
// so cards compose slots and never restate the layout.
// min-w-0 keeps every card exactly as wide as its column: without it,
// non-wrapping content raises the card's min-content size and the flex/grid
// parent lets that card grow wider than its siblings. clip-path (not
// overflow clip) contains runaway content: a pinned FacesheetCardHeader
// must paint its border redraw (1px past the padding box) and corner-mask
// wedges (3px further), and Chrome clips overflow at the padding box
// regardless of overflow-clip-margin — severing both and leaving the card's
// side borders poking past the pinned row's rounded corners. The -4px inset
// clips 4px outside the border box instead, wide enough for the wedges and
// the card's own shadow-sm while still stopping unbreakable content at the
// card edge.
//
// Root of the slot family in this directory: cards arrange the slots
// themselves instead of configuring one component through props. The card is
// also the Collapsible root for FacesheetCardCollapseTrigger and
// FacesheetCardContent — `asChild` so the card element itself carries
// `data-state` and the DOM keeps the shape the pinned-header geometry above is
// measured against.
export function FacesheetCard({
  className,
  defaultOpen = true,
  collapsible = true,
  rumName,
  ...props
}: FacesheetCardProps): JSX.Element {
  const labelId = useId()
  const [open, setOpen] = useState(defaultOpen)
  const shortcutsEnabled = useFeatureFlag(
    featureFlags.facesheetCardCollapseHotkeys
  )
  const facesheetImprovements = useFeatureFlag(
    featureFlags.facesheetImprovements
  )
  const facesheetTab = useFacesheetTab()
  // Collapsible calls this only for the user's own toggles; the hotkey
  // broadcast below sets `open` directly.
  const handleOpenChange = (next: boolean): void => {
    setOpen(next)
    if (facesheetImprovements && rumName) {
      reportFacesheetCardToggled({
        participantId: facesheetTab?.participantId,
        tab: facesheetTab?.tab,
        card: rumName,
        state: next ? 'expanded' : 'collapsed',
      })
    }
  }
  // Cards own their open state because they are composed independently. The
  // store is only a broadcast: subscribe (rather than read the revision during
  // render) so a card mounted after the last shortcut keeps its defaultOpen.
  useEffect(() => {
    if (!shortcutsEnabled || !collapsible) {
      return
    }
    return useFacesheetCardCollapseStore.subscribe((state, previous) => {
      if (state.revision === previous.revision) {
        return
      }
      setOpen(state.open)
    })
  }, [collapsible, shortcutsEnabled])
  const context = useMemo(
    () => ({
      labelId,
      collapsible,
      open: !collapsible || open,
    }),
    [collapsible, labelId, open]
  )

  const card = (
    <Card
      {...props}
      // The spec wins over `className`, not the other way round:
      // FacesheetCardHeader's pinned-row bleed is hard-coded to this padding
      // and gap, so a caller that set its own would tear the pinned border
      // away from the card edge.
      className={cn(
        className,
        'flex min-w-0 flex-col gap-2 p-3 [clip-path:inset(-4px)]',
        // Collapsible puts data-state here, and the triangle two levels down
        // rotates off it.
        'group/facesheet-card'
      )}
    />
  )

  return (
    <FacesheetCardContext.Provider value={context}>
      {collapsible ? (
        <Collapsible asChild open={open} onOpenChange={handleOpenChange}>
          {card}
        </Collapsible>
      ) : (
        card
      )}
    </FacesheetCardContext.Provider>
  )
}
